import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import { styled } from 'styled-components'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartGrid,
  ChartSection,
  ChartTitle,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import { DataTable } from '../DataTable'
import {
  formatPrice,
  formatShortPrice,
  processRoomData,
  calculateKPIs,
  generateInsights,
  getChartColors,
} from './PricePerRoomChart.utils'
import {
  PricePerRoomChartProps,
  PricePerRoomChartRef,
  RoomPriceData,
} from './PricePerRoomChart.types'
import { StatCardVariant } from '../../atoms/StatCard/StatCard.types'

// Styled component for colored table cells
const ColoredCell = styled.div<{
  $backgroundColor?: string
  $textColor?: string
}>`
  padding: 2px 4px;
  border-radius: 3px;
  background-color: ${(props) => props.$backgroundColor || 'transparent'};
  color: ${(props) => props.$textColor || 'inherit'};
  font-weight: ${(props) => (props.$backgroundColor ? '500' : 'inherit')};
  display: inline-block;
  min-width: 100%;
  text-align: center;
  font-size: 11px;
  line-height: 1.2;
`

// Default translation function (returns key as fallback)
const defaultT = (key: string): string => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

// Helper to translate property category names
const translateCategory = (category: string, t: (key: string) => string): string => {
  const key = category.toLowerCase()
  if (key === 'apartment') return t('charts.categories.apartment')
  if (key === 'house') return t('charts.categories.house')
  if (key === 'all types' || key === 'all') return t('charts.labels.allTypes')
  return category
}

const PRICE_TYPE_KEYS = ['median', 'average'] as const

export const PricePerRoomChart = forwardRef<PricePerRoomChartRef, PricePerRoomChartProps>(
  (
    {
      data = [],
      loading = false,
      error = null,
      priceType = 'median',
      onPriceTypeChange,
      hidePriceTypeButtons = false,
      isRental = false,
      showKPIs = true,
      showInsights = true,
      showControls = true,
      t = defaultT,
    },
    ref
  ) => {
    // Translation helpers
    const priceLabel = isRental ? t('charts.labels.rent') : t('charts.labels.price')
    const priceOrRent = isRental ? 'rent' : 'price'
    const priceTypeLabel = t(`charts.priceTypes.${priceType}`)
    const chartRef = useRef<HTMLDivElement>(null)
    const chartInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    // Helper function to group rooms into categories
    const getRoomCategory = (rooms: number): string => {
      if (rooms <= 1.5) return '≤ 1.5'
      if (rooms <= 2.5) return '2 - 2.5'
      if (rooms <= 3.5) return '3 - 3.5'
      if (rooms <= 4.5) return '4 - 4.5'
      if (rooms <= 5.5) return '5 - 5.5'
      if (rooms <= 6.5) return '6 - 6.5'
      return '7+'
    }

    useEffect(() => {
      if (!loading && !error && processedData.length > 0) {
        createChart()
      }

      return () => {
        chartInstance.current?.dispose()
      }
    }, [data, priceType, loading, error, isRental])

    // Use ResizeObserver to properly resize chart when container size changes
    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        chartInstance.current?.resize()
      })

      if (chartRef.current) {
        resizeObserver.observe(chartRef.current)
      }

      return () => resizeObserver.disconnect()
    }, [])

    // process data
    const processedData = processRoomData(data, priceType)
    // Calculate and set KPIs
    const kpis = calculateKPIs(processedData, priceType)
    const kpiData = [
      {
        id: 'total-listings',
        label: t('charts.kpis.totalListings'),
        value: kpis.totalListings.toLocaleString(),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'avg-price',
        label: isRental ? t(`charts.kpis.${priceType}Rent`) : t(`charts.kpis.${priceType}Price`),
        value: formatPrice(kpis.avgPrice, isRental),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'avg-rooms',
        label: t('charts.kpis.averageRooms'),
        value: kpis.avgRooms.toFixed(1),
        variant: 'default' as StatCardVariant,
      },
      {
        id: 'price-per-room',
        label: isRental
          ? t(`charts.kpis.${priceType === 'median' ? 'medianRentPerRoom' : 'avgRentPerRoom'}`)
          : t(`charts.kpis.${priceType === 'median' ? 'medianPricePerRoom' : 'avgPricePerRoom'}`),
        value: formatPrice(kpis.avgPricePerRoom, isRental),
        variant: 'default' as StatCardVariant,
      },
    ]

    // Generate insights
    const insights = generateInsights(processedData, priceType, isRental, t)

    // Expose methods via ref for PDF export
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (chartInstance.current) {
          chartInstance.current.resize()
          return chartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getKPIs: () => kpiData,
      getInsights: () => insights,
      getTableData: () => {
        // Helper function to convert rgba green gradient to solid RGB (for PDF)
        const getCountPastelColor = (
          value: number,
          minCount: number,
          maxCount: number
        ): { backgroundColor: string; textColor: string } => {
          if (minCount === maxCount) return { backgroundColor: '#f3f4f6', textColor: '#374151' }

          const position = (value - minCount) / (maxCount - minCount)
          // Green gradient: more listings = darker green
          const intensity = 0.1 + position * 0.2
          // Convert rgba(34, 197, 94, intensity) to solid RGB
          const r = Math.round(34 * intensity + 255 * (1 - intensity))
          const g = Math.round(197 * intensity + 255 * (1 - intensity))
          const b = Math.round(94 * intensity + 255 * (1 - intensity))
          return {
            backgroundColor: `rgb(${r}, ${g}, ${b})`,
            textColor: '#166534',
          }
        }

        const dataWithQuantiles = processedData.filter((d) => d.quantiles)
        if (dataWithQuantiles.length === 0) return []

        // Group rooms into categories (same logic as renderQuantileTable)
        const getRoomCategory = (rooms: number): string => {
          if (rooms <= 1.5) return '≤ 1.5'
          if (rooms <= 2.5) return '2 - 2.5'
          if (rooms <= 3.5) return '3 - 3.5'
          if (rooms <= 4.5) return '4 - 4.5'
          if (rooms <= 5.5) return '5 - 5.5'
          if (rooms <= 6.5) return '6 - 6.5'
          return '7+'
        }

        // Group data by category and room group
        const groupedData: { [key: string]: any[] } = {}
        dataWithQuantiles.forEach((d) => {
          const roomGroup = getRoomCategory(d.rooms)
          const key = `${d.category}_${roomGroup}`
          if (!groupedData[key]) {
            groupedData[key] = []
          }
          groupedData[key].push(d)
        })

        // Calculate aggregated quantiles for each group
        const aggregatedData = Object.entries(groupedData).map(([key, items]) => {
          const [category, roomGroup] = key.split('_')
          const totalCount = items.reduce((sum, item) => sum + item.count, 0)

          const q10Values = items.map((item) => item.quantiles.q10 * item.count)
          const q30Values = items.map((item) => item.quantiles.q30 * item.count)
          const q50Values = items.map((item) => item.quantiles.q50 * item.count)
          const q70Values = items.map((item) => item.quantiles.q70 * item.count)
          const q90Values = items.map((item) => item.quantiles.q90 * item.count)

          return {
            category,
            roomGroup,
            q10: Math.round(q10Values.reduce((a, b) => a + b, 0) / totalCount),
            q30: Math.round(q30Values.reduce((a, b) => a + b, 0) / totalCount),
            q50: Math.round(q50Values.reduce((a, b) => a + b, 0) / totalCount),
            q70: Math.round(q70Values.reduce((a, b) => a + b, 0) / totalCount),
            q90: Math.round(q90Values.reduce((a, b) => a + b, 0) / totalCount),
            count: totalCount,
          }
        })

        // Add "All rooms" row for each category
        const categories = [...new Set(dataWithQuantiles.map((d) => d.category))]
        const allRoomsData = categories.map((category) => {
          const categoryItems = dataWithQuantiles.filter((d) => d.category === category)
          const totalCount = categoryItems.reduce((sum, item) => sum + item.count, 0)

          const q10 = Math.round(
            categoryItems.reduce((sum, item) => sum + item.quantiles.q10 * item.count, 0) /
              totalCount
          )
          const q30 = Math.round(
            categoryItems.reduce((sum, item) => sum + item.quantiles.q30 * item.count, 0) /
              totalCount
          )
          const q50 = Math.round(
            categoryItems.reduce((sum, item) => sum + item.quantiles.q50 * item.count, 0) /
              totalCount
          )
          const q70 = Math.round(
            categoryItems.reduce((sum, item) => sum + item.quantiles.q70 * item.count, 0) /
              totalCount
          )
          const q90 = Math.round(
            categoryItems.reduce((sum, item) => sum + item.quantiles.q90 * item.count, 0) /
              totalCount
          )

          return {
            category,
            roomGroup: 'All rooms',
            q10,
            q30,
            q50,
            q70,
            q90,
            count: totalCount,
          }
        })

        // Combine and sort the data
        const roomOrder = ['≤ 1.5', '2 - 2.5', '3 - 3.5', '4 - 4.5', '5 - 5.5', '6 - 6.5', '7+']
        const finalData = [...allRoomsData, ...aggregatedData].sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category)
          }
          if (a.roomGroup === 'All rooms') return -1
          if (b.roomGroup === 'All rooms') return 1
          return roomOrder.indexOf(a.roomGroup) - roomOrder.indexOf(b.roomGroup)
        })

        // Calculate min/max for count color scaling
        const counts = finalData.map((d) => d.count)
        const minCount = Math.min(...counts)
        const maxCount = Math.max(...counts)

        // Format the data for PDF table
        return finalData.map((d) => {
          const isAllRooms = d.roomGroup === 'All rooms'
          const countColors = getCountPastelColor(d.count, minCount, maxCount)

          return {
            category: isAllRooms
              ? {
                  value: translateCategory(d.category, t),
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : translateCategory(d.category, t),
            rooms: isAllRooms
              ? {
                  value: d.roomGroup,
                  backgroundColor: '#e0f2fe',
                  textColor: '#0c4a6e',
                }
              : d.roomGroup,
            q10: formatShortPrice(d.q10, isRental),
            q30: formatShortPrice(d.q30, isRental),
            q50: {
              value: formatShortPrice(d.q50, isRental),
              backgroundColor: '#fef3c7',
              textColor: '#92400e',
            },
            q70: formatShortPrice(d.q70, isRental),
            q90: formatShortPrice(d.q90, isRental),
            count: {
              value: d.count.toLocaleString(),
              backgroundColor: countColors.backgroundColor,
              textColor: countColors.textColor,
            },
          }
        })
      },
    }))

    const createChart = () => {
      if (!chartRef.current) return

      if (chartInstance.current) {
        chartInstance.current.dispose()
      }

      chartInstance.current = safeChartInit(chartRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current ? 1 : window.devicePixelRatio || 1,
      })

      if (isPrintingRef.current) {
        setTimeout(() => {
          chartInstance.current?.resize()
        }, 50)
      }

      const colors = getChartColors()
      const option = createScatterOption(colors)
      chartInstance.current.setOption(option)
    }

    // Helper function to aggregate data by room category
    const aggregateByRoomGroup = (
      data: RoomPriceData[],
      priceField: 'medianPrice' | 'avgPrice'
    ) => {
      const roomOrder = ['≤ 1.5', '2 - 2.5', '3 - 3.5', '4 - 4.5', '5 - 5.5', '6 - 6.5', '7+']
      const grouped: Record<string, RoomPriceData[]> = {}

      data.forEach((d) => {
        const category = getRoomCategory(d.rooms)
        if (!grouped[category]) grouped[category] = []
        grouped[category].push(d)
      })

      return roomOrder
        .filter((cat) => grouped[cat]?.length > 0)
        .map((roomGroup) => {
          const items = grouped[roomGroup]
          const totalCount = items.reduce((sum, d) => sum + d.count, 0)
          return {
            roomGroup,
            price: items.reduce((sum, d) => sum + d[priceField] * d.count, 0) / totalCount,
            count: totalCount,
            min: Math.min(...items.map((d) => d.min)),
            max: Math.max(...items.map((d) => d.max)),
            stdDev: Math.sqrt(
              items.reduce((sum, d) => sum + Math.pow(d.stdDev, 2) * d.count, 0) / totalCount
            ),
          }
        })
    }

    const createScatterOption = (colors: any) => {
      const apartmentData = processedData.filter((d) => d.category === 'Apartment')
      const houseData = processedData.filter((d) => d.category === 'House')
      const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

      // Aggregate data by room groups
      const aggregatedApartmentData = aggregateByRoomGroup(apartmentData, priceField)
      const aggregatedHouseData = aggregateByRoomGroup(houseData, priceField)

      // Get all room groups that have data (for x-axis categories)
      const roomOrder = ['≤ 1.5', '2 - 2.5', '3 - 3.5', '4 - 4.5', '5 - 5.5', '6 - 6.5', '7+']
      const allRoomGroups = new Set([
        ...aggregatedApartmentData.map((d) => d.roomGroup),
        ...aggregatedHouseData.map((d) => d.roomGroup),
      ])
      const xAxisCategories = roomOrder.filter((cat) => allRoomGroups.has(cat))

      // Calculate max count for visualMap
      const allAggregatedData = [...aggregatedApartmentData, ...aggregatedHouseData]
      const maxCount =
        allAggregatedData.length > 0 ? Math.max(...allAggregatedData.map((d) => d.count)) : 1

      const option: any = {
        backgroundColor: 'transparent',
        title: {
          text: t(`charts.titles.roomCountVs${isRental ? 'Rent' : 'Price'}`, {
            priceType: priceTypeLabel,
          }),
          left: 'center',
          top: 16,
          textStyle: {
            fontSize: 18,
            fontWeight: 600,
            color: '#1a1a1a',
          },
        },
        tooltip: {
          trigger: 'item',
          appendTo: () => document.body,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          formatter: (params: any) => {
            const d = params.data
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${params.seriesName}</div>
              <div style="display: grid; gap: 4px;">
                <div>${t('charts.labels.rooms')}: ${d[0]}</div>
                <div>${priceType === 'median' ? 'Median' : 'Avg'} ${isRental ? 'Rent' : 'Price'}: ${formatPrice(d[1], isRental)}</div>
                <div>${t('charts.labels.listings')}: ${d[2].toLocaleString()}</div>
                <div>${t('charts.labels.range')}: ${formatPrice(d[3], isRental)} - ${formatPrice(d[4], isRental)}</div>
                <div>${t('charts.labels.stdDev')}: ${formatPrice(d[5], isRental)}</div>
              </div>
            </div>
          `
          },
        },
        legend: {
          data: [t('charts.categories.apartment'), t('charts.categories.house')],
          bottom: 20,
          itemGap: 30,
          textStyle: {
            fontSize: 14,
            color: '#6c757d',
          },
        },
        grid: {
          left: 40,
          right: 10,
          top: 60,
          bottom: 110,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: xAxisCategories,
          name: t('charts.labels.numberOfRooms'),
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 14,
            color: '#6c757d',
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          axisLabel: {
            fontSize: 12,
            color: '#6c757d',
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'value',
          name: `${priceTypeLabel} ${priceLabel} (CHF)`,
          nameLocation: 'middle',
          nameGap: 60,
          nameTextStyle: {
            fontSize: 14,
            color: '#6c757d',
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          axisLabel: {
            fontSize: 12,
            color: '#6c757d',
            formatter: (value: number) => formatShortPrice(value, isRental),
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: t('charts.categories.apartment'),
            type: 'scatter',
            data: aggregatedApartmentData.map((d) => [
              d.roomGroup,
              d.price,
              d.count,
              d.min,
              d.max,
              d.stdDev,
            ]),
            symbolSize: (data: number[]) => {
              return Math.sqrt(data[2]) * 3 + 10
            },
            itemStyle: {
              color: colors.apartment,
              opacity: 0.7,
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                shadowBlur: 10,
                shadowColor: 'rgba(37, 99, 235, 0.3)',
              },
            },
          },
          {
            name: t('charts.categories.house'),
            type: 'scatter',
            data: aggregatedHouseData.map((d) => [
              d.roomGroup,
              d.price,
              d.count,
              d.min,
              d.max,
              d.stdDev,
            ]),
            symbolSize: (data: number[]) => {
              return Math.sqrt(data[2]) * 3 + 10
            },
            itemStyle: {
              color: colors.house,
              opacity: 0.7,
            },
            emphasis: {
              itemStyle: {
                opacity: 1,
                shadowBlur: 10,
                shadowColor: 'rgba(16, 185, 129, 0.3)',
              },
            },
          },
        ],
        visualMap: {
          show: false,
          dimension: 2,
          min: 0,
          max: maxCount,
          inRange: {
            symbolSize: [10, 60],
          },
        },
      }

      // Add trend lines (using aggregated data with categorical x-axis)
      if (aggregatedApartmentData.length >= 2) {
        option.series.push({
          name: t('charts.categories.apartmentTrend'),
          type: 'line',
          data: aggregatedApartmentData.map((d) => [d.roomGroup, d.price]),
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: colors.apartment,
            width: 2,
            type: 'dashed',
          },
          silent: true,
        })
      }

      if (aggregatedHouseData.length >= 2) {
        option.series.push({
          name: t('charts.categories.houseTrend'),
          type: 'line',
          data: aggregatedHouseData.map((d) => [d.roomGroup, d.price]),
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: colors.house,
            width: 2,
            type: 'dashed',
          },
          silent: true,
        })
      }

      return option
    }

    // Match the Building Permits report, which prints cleanly: do NOT touch
    // the charts on print. The previous beforeprint/afterprint/matchMedia
    // handlers called chart.resize() + setOption() *during* the print
    // snapshot, which made Chrome serialise the canvas as a masked vector
    // figure (the white boxes in macOS Preview / Quick Look). Building
    // Permits charts do nothing on print and rasterise cleanly. The toolbox
    // is already hidden at creation (showToolbox=false in the report) and the
    // print layout keeps the same width, so no print-time resize is needed.
    useEffect(() => {
      const handleResize = () => {
        chartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    // Prepare quantile table data
    const renderQuantileTable = () => {
      const dataWithQuantiles = processedData.filter((d) => d.quantiles)

      if (dataWithQuantiles.length === 0) {
        return null
      }

      // Group data by category and room group
      const groupedData: { [key: string]: any[] } = {}

      dataWithQuantiles.forEach((d) => {
        const roomGroup = getRoomCategory(d.rooms)
        const key = `${d.category}_${roomGroup}`

        if (!groupedData[key]) {
          groupedData[key] = []
        }
        groupedData[key].push(d)
      })

      // Calculate aggregated quantiles for each group
      const aggregatedData = Object.entries(groupedData).map(([key, items]) => {
        const [category, roomGroup] = key.split('_')
        const totalCount = items.reduce((sum, item) => sum + item.count, 0)

        // Calculate weighted quantiles
        const q10Values = items.map((item) => item.quantiles.q10 * item.count)
        const q30Values = items.map((item) => item.quantiles.q30 * item.count)
        const q50Values = items.map((item) => item.quantiles.q50 * item.count)
        const q70Values = items.map((item) => item.quantiles.q70 * item.count)
        const q90Values = items.map((item) => item.quantiles.q90 * item.count)

        return {
          category,
          roomGroup,
          q10: Math.round(q10Values.reduce((a, b) => a + b, 0) / totalCount),
          q30: Math.round(q30Values.reduce((a, b) => a + b, 0) / totalCount),
          q50: Math.round(q50Values.reduce((a, b) => a + b, 0) / totalCount),
          q70: Math.round(q70Values.reduce((a, b) => a + b, 0) / totalCount),
          q90: Math.round(q90Values.reduce((a, b) => a + b, 0) / totalCount),
          count: totalCount,
        }
      })

      // Add "All rooms" row for each category
      const categories = [...new Set(dataWithQuantiles.map((d) => d.category))]
      const allRoomsData = categories.map((category) => {
        const categoryItems = dataWithQuantiles.filter((d) => d.category === category)
        const totalCount = categoryItems.reduce((sum, item) => sum + item.count, 0)

        const q10 = Math.round(
          categoryItems.reduce((sum, item) => sum + item.quantiles.q10 * item.count, 0) / totalCount
        )
        const q30 = Math.round(
          categoryItems.reduce((sum, item) => sum + item.quantiles.q30 * item.count, 0) / totalCount
        )
        const q50 = Math.round(
          categoryItems.reduce((sum, item) => sum + item.quantiles.q50 * item.count, 0) / totalCount
        )
        const q70 = Math.round(
          categoryItems.reduce((sum, item) => sum + item.quantiles.q70 * item.count, 0) / totalCount
        )
        const q90 = Math.round(
          categoryItems.reduce((sum, item) => sum + item.quantiles.q90 * item.count, 0) / totalCount
        )

        return {
          category,
          roomGroup: 'All rooms',
          q10,
          q30,
          q50,
          q70,
          q90,
          count: totalCount,
        }
      })

      // Combine and sort the data
      const finalData = [...allRoomsData, ...aggregatedData].sort((a, b) => {
        // Sort by category first
        if (a.category !== b.category) {
          return a.category.localeCompare(b.category)
        }
        // Then by room group (All rooms first)
        if (a.roomGroup === 'All rooms') return -1
        if (b.roomGroup === 'All rooms') return 1

        // Sort room groups in order
        const roomOrder = ['≤ 1.5', '2 - 2.5', '3 - 3.5', '4 - 4.5', '5 - 5.5', '6 - 6.5', '7+']
        return roomOrder.indexOf(a.roomGroup) - roomOrder.indexOf(b.roomGroup)
      })

      const quantileData = finalData.map((d) => ({
        category: translateCategory(d.category, t),
        rooms: d.roomGroup,
        q10: d.q10,
        q30: d.q30,
        q50: d.q50,
        q70: d.q70,
        q90: d.q90,
        count: d.count,
        isAllRooms: d.roomGroup === 'All rooms',
      }))

      // Get min/max for color scaling
      const counts = quantileData.map((d) => d.count)
      const minCount = Math.min(...counts)
      const maxCount = Math.max(...counts)

      // Helper function to get color based on value position in range
      const getCountColor = (value: number): { backgroundColor: string; textColor: string } => {
        if (minCount === maxCount) return { backgroundColor: '#f3f4f6', textColor: '#374151' }

        const position = (value - minCount) / (maxCount - minCount)

        // Green gradient: more listings = darker green (more reliable data)
        const intensity = 0.1 + position * 0.2
        return {
          backgroundColor: `rgba(34, 197, 94, ${intensity})`,
          textColor: '#166534',
        }
      }

      const columns = [
        {
          key: 'category',
          label: t('charts.labels.propertyType'),
          sortable: true,
          render: (item: any) => {
            const isAllRooms = item.isAllRooms
            return (
              <ColoredCell
                $backgroundColor={isAllRooms ? '#e0f2fe' : undefined}
                $textColor={isAllRooms ? '#0c4a6e' : undefined}
              >
                {item.category}
              </ColoredCell>
            )
          },
        },
        {
          key: 'rooms',
          label: t('charts.labels.rooms'),
          sortable: true,
          render: (item: any) => {
            const isAllRooms = item.isAllRooms
            return (
              <ColoredCell
                $backgroundColor={isAllRooms ? '#e0f2fe' : undefined}
                $textColor={isAllRooms ? '#0c4a6e' : undefined}
              >
                {item.rooms}
              </ColoredCell>
            )
          },
        },
        {
          key: 'q10',
          label: t('charts.labels.percentile10'),
          sortable: true,
          render: (item: any) => formatShortPrice(item.q10, isRental),
        },
        {
          key: 'q30',
          label: t('charts.labels.percentile30'),
          sortable: true,
          render: (item: any) => formatShortPrice(item.q30, isRental),
        },
        {
          key: 'q50',
          label: t('charts.labels.percentile50Median'),
          sortable: true,
          render: (item: any) => (
            <ColoredCell $backgroundColor="#fef3c7" $textColor="#92400e">
              {formatShortPrice(item.q50, isRental)}
            </ColoredCell>
          ),
        },
        {
          key: 'q70',
          label: t('charts.labels.percentile70'),
          sortable: true,
          render: (item: any) => formatShortPrice(item.q70, isRental),
        },
        {
          key: 'q90',
          label: t('charts.labels.percentile90'),
          sortable: true,
          render: (item: any) => formatShortPrice(item.q90, isRental),
        },
        {
          key: 'count',
          label: t('charts.labels.count'),
          sortable: true,
          render: (item: any) => {
            const colors = getCountColor(item.count)
            return (
              <ColoredCell $backgroundColor={colors.backgroundColor} $textColor={colors.textColor}>
                {item.count.toLocaleString()}
              </ColoredCell>
            )
          },
        },
      ]

      return (
        <>
          <Text variant="body2" weight="semibold" style={{ marginBottom: '8px' }}>
            {isRental ? t('charts.titles.rentByRooms') : t('charts.titles.priceByRooms')}
          </Text>
          <DataTable
            data={quantileData}
            columns={columns}
            stickyHeader={false}
            compact={true}
            dense={true}
          />
        </>
      )
    }

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('charts.states.loadingPricePerRoom')}
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (processedData.length === 0) {
      return (
        <NotAvailable
          title={t('charts.empty.title')}
          description={t(`charts.empty.${priceOrRent}PerRoom`)}
        />
      )
    }

    return (
      <PrintWrapper>
        {showKPIs && kpiData.length > 0 && (
          <Box marginBottom="30px">
            <StatsContainer fullWidth stats={kpiData} />
          </Box>
        )}

        {showControls && onPriceTypeChange && !hidePriceTypeButtons && (
          <Box marginBottom="16px">
            <Box display="flex" justifyContent="flex-end">
              <ButtonGroup>
                {PRICE_TYPE_KEYS.map((pt) => (
                  <Button
                    key={pt}
                    variant={priceType === pt ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => onPriceTypeChange(pt)}
                  >
                    {t(`charts.priceTypes.${pt}`)}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
        )}

        {/* Chart and Quantile Table side by side */}
        {processedData && processedData.length > 0 && processedData.some((d) => d.quantiles) ? (
          <ChartGrid columns={2}>
            <ChartSection>
              <ChartContainer height={480} ref={chartRef} />
            </ChartSection>
            <ChartSection>
              <Box>{renderQuantileTable()}</Box>
            </ChartSection>
          </ChartGrid>
        ) : (
          <Box marginBottom="30px">
            <ChartSection>
              <ChartContainer height={480} ref={chartRef} />
            </ChartSection>
          </Box>
        )}

        {showInsights && insights.length > 0 && (
          <Box>
            <ChartTitle style={{ marginBottom: '24px' }}>
              {t('charts.insights.keyInsights')}
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

PricePerRoomChart.displayName = 'PricePerRoomChart'
